import { useEffect, useState } from 'react';
import type { ApiClient } from '../../api';
import { Drawer, ErrorState, Feld, LoadingState } from '../../muster';

/** Die beiden Lüftungsschemata (LUE-ASL, LUE-SSL). */
export type Schemaart = 'anlagenschema' | 'strangschema';

export const SCHEMA_TEXT: Record<Schemaart, string> = {
  anlagenschema: 'Anlagenschema',
  strangschema: 'Strangschema',
};

const SCHEMA_HINWEIS: Record<Schemaart, string> = {
  anlagenschema: 'Die Bauteile des Geräts in Strömungsrichtung mit ihren Druckverlusten.',
  strangschema: 'Je Weg ein Blatt: Abgänge und Auslässe mit Teilstrecke, Volumenstrom, Raum, Druckverlust und Pegel.',
};

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * TV6d - VORSCHAU DER LÜFTUNGSSCHEMATA (ASL/SSL) AUF DER SEITE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bisher gab es Anlagen- und Strangschema nur als Herunterladen (Druckverlust).
 * Hier stehen sie je Anlage in einem Schubfach, als die PDF-Datei selbst: Die
 * Vorschau ist genau das Dokument, das man herunterlädt - keine zweite
 * Darstellung, die vom Bericht abweichen könnte. Die Route ist dieselbe und bis
 * TX2 ohne Register (TX-STANDARD S3.1, befristete Ausnahme): Eine Vorschau zieht
 * keine Dokumentnummer.
 */
export function Schemavorschau({ api, projektId, anlage, schliessen }: {
  api: ApiClient;
  projektId: string;
  anlage: string;
  schliessen: () => void;
}) {
  const [art, setArt] = useState<Schemaart>('anlagenschema');
  const [datei, setDatei] = useState<{ url: string; name: string } | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [versuch, setVersuch] = useState(0);

  useEffect(() => {
    let aktiv = true;
    let url: string | null = null;
    setDatei(null);
    setFehler(null);

    api.lueftungSchemaVorschau(projektId, art, anlage)
      .then(({ datei: blob, name }) => {
        if (!aktiv) return;
        url = URL.createObjectURL(blob);
        setDatei({ url, name });
      })
      .catch((e) => { if (aktiv) setFehler(e); });

    return () => {
      aktiv = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [api, projektId, art, anlage, versuch]);

  return (
    <Drawer
      offen
      titel={`Schemata ${anlage}`}
      beschreibung="Vorschau des Dokuments, wie es heruntergeladen wird - ohne Dokumentnummer (Route ohne Register bis TX2)."
      beiSchliessen={schliessen}
      breite="720px"
      fuss={(
        <>
          {datei && <a className="m-knopf m-knopf-haupt" href={datei.url} download={datei.name}>{SCHEMA_TEXT[art]} herunterladen</a>}
          <button type="button" className="m-knopf" onClick={schliessen}>Schließen</button>
        </>
      )}
    >
      <Feld
        art="radio"
        beschriftung="Schema"
        wert={art}
        optionen={(Object.keys(SCHEMA_TEXT) as Schemaart[]).map((a) => ({
          wert: a, beschriftung: SCHEMA_TEXT[a], erklaerung: SCHEMA_HINWEIS[a],
        }))}
        beiAenderung={(w) => setArt(w as Schemaart)}
        breite="voll"
      />

      {fehler !== null && (
        <ErrorState
          klein
          titel={`Das ${SCHEMA_TEXT[art]} konnte nicht erzeugt werden.`}
          meldung="Ohne gelesenes Kanalnetz oder Gerätedaten gibt es kein Schema."
          technisch={fehler}
          beiErneutVersuchen={() => setVersuch((n) => n + 1)}
        />
      )}
      {fehler === null && !datei && <LoadingState text={`${SCHEMA_TEXT[art]} wird erzeugt …`} zeilen={6} />}
      {datei && <iframe className="lue-pdf-vorschau" title={`${SCHEMA_TEXT[art]} ${anlage}`} src={datei.url} />}
    </Drawer>
  );
}
